import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Select,
  Switch,
  Text,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { LEAD_TIME_FORMAT_OPTIONS } from '@lib/enums/lead-time-format';
import { SELLING_UOM_OPTIONS } from '@lib/enums/selling-unit-of-measure';
import { SiteCode, WonLossStatus } from '@snap-types/models/enums';
import { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import { formatWonLossStatus } from '@snap-types/models/visual/user-def-fields';
import { BoxAlert } from '@ui/components/Alert';
import { Show } from '@ui/components/Show';
import { isAutoConfiguratorSite } from '@ui/features/alpha/constants';
import { supportsConfigurator } from '@ui/features/configurator/data';
import { useCurrentSiteCode } from '@ui/hooks/useCurrentSite';
import { get, useFormContext } from 'react-hook-form';

import { ConfiguratorMissingPriceStrategyWarning } from './components/ConfiguratorMissingPriceStrategyWarning';
import { ControlGroupCard } from './components/ControlGroupCard';
import { FeatureVisibleSelect } from './components/FeatureVisibleSelect';
import { ImportFilesConfiguration } from './components/ImportFilesConfiguration';

export function BasicConfigPanel() {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();
  const siteCode = useCurrentSiteCode();
  const configuratorUnsupported = !supportsConfigurator(siteCode);

  return (
    <Flex direction="column" gap={4} w="full">
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          lg: '1fr 1fr',
          xl: '1fr 1fr 1fr',
        }}
        gap={4}
        mb={4}
      >
        <ControlGroupCard
          title="Default Won/Loss Status"
          dataGroupScope="default-won-loss-status"
        >
          <FormControl>
            <Flex flexDirection="row" gap={2}>
              <Switch
                {...form.register('useQuoteWonLossStatus')}
                id="use_quote_won_loss_status"
              />
              <FormLabel htmlFor="use_quote_won_loss_status">
                Use Default
              </FormLabel>
            </Flex>
          </FormControl>

          <FormControl isInvalid={!!form.formState.errors.defaultWonLossStatus}>
            <FormLabel htmlFor="default_won_loss_status_input">
              Default Value:
            </FormLabel>

            <Select
              {...form.register('defaultWonLossStatus')}
              id="default_won_loss_status_input"
              isDisabled={!form.watch('useQuoteWonLossStatus')}
            >
              {Object.entries(WonLossStatus).map(([key, value]) => (
                <option key={key} value={key}>
                  {formatWonLossStatus(value)}
                </option>
              ))}
            </Select>

            <ErrorMessage
              name="defaultWonLossStatus"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />
          </FormControl>
        </ControlGroupCard>

        <ControlGroupCard title="Quote Imports" dataGroupScope="quote-imports">
          <FormControl>
            <Flex flexDirection="row" gap={2}>
              <Switch
                {...form.register('useImportLineItems')}
                id="use_import_line_items"
              />
              <FormLabel htmlFor="use_import_line_items">
                Use Import Line Items
              </FormLabel>
            </Flex>
          </FormControl>

          <FormControl>
            <Flex flexDirection="row" gap={2}>
              <Switch
                {...form.register('consolidateImportedLineItems')}
                id="consolidate_imported_line_items"
                isDisabled={!form.watch('useImportLineItems')}
              />
              <FormLabel htmlFor="consolidate_imported_line_items">
                Consolidate Imported Line Items
              </FormLabel>
            </Flex>
          </FormControl>

          <ImportFilesConfiguration />
        </ControlGroupCard>

        <ControlGroupCard
          title="Quote Formatting"
          dataGroupScope="quote-formatting"
        >
          <FormControl isInvalid={!!form.formState.errors.decimalPlaces}>
            <FormLabel htmlFor="decimal_places_input">
              Decimal Places:
            </FormLabel>

            {/* @NOTE(shawk): Ideally, this should be limited by the precision
          we set at the database column data type level. */}
            <Input
              id="decimal_places_input"
              type="number"
              inputMode="numeric"
              {...form.register('decimalPlaces')}
              min={0}
              max={10}
            />

            <ErrorMessage
              name="decimalPlaces"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />

            <FormHelperText>
              We recommend setting this no lower than 2.
            </FormHelperText>
          </FormControl>

          <FormControl
            isInvalid={!!get(form.formState.errors, 'sellingUnitOfMeasure')}
          >
            <FormLabel htmlFor="selling_unit_of_measure_input">
              Selling Unit of Measure:
            </FormLabel>

            <Select
              {...form.register('sellingUnitOfMeasure')}
              id="selling_unit_of_measure_input"
            >
              {SELLING_UOM_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <ErrorMessage
              name="sellingUnitOfMeasure"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="lead_time_format_input">
              Show Lead Time Fields on PDF:
            </FormLabel>

            <Select
              {...form.register('leadTimeFormat')}
              id="lead_time_format_input"
            >
              {LEAD_TIME_FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <ErrorMessage
              name="leadTimeFormat"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />
          </FormControl>

          {/* @NOTE(shawk): For now, we're keeping this simple. If we need
            more flexibility/expressiveness we can reconsider how we structure
            the configuration and rendering logic. */}
          <Show when={showPartRequirementFixedCost(siteCode)}>
            <FormControl
              isInvalid={
                !!get(form.formState.errors, 'defaultPartRequirementFixedCost')
              }
            >
              <FormLabel htmlFor="default_part_requirement_fixed_cost_input">
                Part Requirement Fixed Cost (default):
              </FormLabel>

              <Input
                id="default_part_requirement_fixed_cost_input"
                type="number"
                inputMode="numeric"
                {...form.register('defaultPartRequirementFixedCost')}
                min={0}
              />

              <ErrorMessage
                name="defaultPartRequirementFixedCost"
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />
            </FormControl>
          </Show>
          <FormControl>
            <FormLabel htmlFor="show_cost_fields_for_fixed_price_strategies_input">
              Show Cost Fields for Fixed Price Strategies:
            </FormLabel>

            <Switch
              id="show_cost_fields_for_fixed_price_strategies_input"
              {...form.register('showCostFieldsForFixedPriceStrategies')}
            />
          </FormControl>
        </ControlGroupCard>

        <ControlGroupCard
          title="Week Definitions"
          dataGroupScope="week-definitions"
        >
          <FormControl
            isInvalid={!!form.formState.errors.productionDaysPerWeek}
          >
            <FormLabel htmlFor="production_days_per_week_input">
              Production Days Per Week:
            </FormLabel>

            <Input
              id="production_days_per_week_input"
              type="number"
              inputMode="numeric"
              {...form.register('productionDaysPerWeek')}
              min={1}
              max={7}
            />

            <ErrorMessage
              name="productionDaysPerWeek"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />

            <FormHelperText>
              How many days a week the floor runs. Lead times are stored in
              production days, so this converts them to and from weeks
              everywhere they are entered or quoted.
            </FormHelperText>
          </FormControl>

          <FormControl isInvalid={!!form.formState.errors.transitDaysPerWeek}>
            <FormLabel htmlFor="transit_days_per_week_input">
              Transit Days Per Week:
            </FormLabel>

            <Input
              id="transit_days_per_week_input"
              type="number"
              inputMode="numeric"
              {...form.register('transitDaysPerWeek')}
              min={1}
              max={7}
            />

            <ErrorMessage
              name="transitDaysPerWeek"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />

            <FormHelperText>
              The week an outside service operation&apos;s transit days are
              recorded on in Visual. Seven if the carrier runs weekends.
            </FormHelperText>
          </FormControl>

          <FormControl isInvalid={!!form.formState.errors.planningDaysPerWeek}>
            <FormLabel htmlFor="planning_days_per_week_input">
              Planning Days Per Week:
            </FormLabel>

            <Input
              id="planning_days_per_week_input"
              type="number"
              inputMode="numeric"
              {...form.register('planningDaysPerWeek')}
              min={1}
              max={7}
            />

            <ErrorMessage
              name="planningDaysPerWeek"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />

            <FormHelperText>
              The week a Visual planning lead time is recorded on. This is the
              material wait a quantity-aware lead time is built from.
            </FormHelperText>
          </FormControl>

          <Divider />

          <Text fontSize="sm">
            Shift Hours, Buffer Days, and Move Days are not set here. They are
            set per lead time strategy on the Price Strategies tab.
          </Text>
        </ControlGroupCard>

        <ControlGroupCard
          title="System Configuration"
          dataGroupScope="system-configuration"
        >
          <FormControl>
            <Flex flexDirection="row" gap={2}>
              <Switch
                {...form.register('useConfigurator')}
                id="use_configurator"
                isDisabled={configuratorUnsupported}
              />
              <FormLabel htmlFor="use_configurator">Use Configurator</FormLabel>
            </Flex>

            <Show when={siteCode === SiteCode.USA}>
              <FormHelperText>
                Configurator is not available for USA. SNAP no longer supports
                configuring Stud parts.
              </FormHelperText>
            </Show>
          </FormControl>

          {configuratorUnsupported ? (
            <Box mb={4}>
              <BoxAlert
                status="info"
                description="The SNAP application does not support Configurator for this site."
              />
            </Box>
          ) : (
            <ConfiguratorMissingPriceStrategyWarning />
          )}

          <Show when={isAutoConfiguratorSite(siteCode)}>
            <FormControl>
              <FormLabel htmlFor="alpha_visible_input">
                Auto Configurator Visibility
              </FormLabel>

              <FeatureVisibleSelect
                {...form.register('alphaVisible')}
                id="alpha_visible_input"
              />
            </FormControl>
          </Show>

          <FormControl>
            <FormLabel htmlFor="duplicate_quote_visible_input">
              Duplicate Quote Visibility
            </FormLabel>

            <FeatureVisibleSelect
              {...form.register('duplicateQuoteVisible')}
              id="duplicate_quote_visible_input"
            />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor="search_existing_customer_part_ids_visible_input">
              Automatically Retrieve Customer Part ID
            </FormLabel>

            <FeatureVisibleSelect
              {...form.register('searchExistingCustomerPartIdsVisible')}
              id="search_existing_customer_part_ids_visible_input"
            />
          </FormControl>
        </ControlGroupCard>
      </Grid>
      {/*
          @see {@link https://github.com/mwcomponents/quickquote/issues/2718}

          <FormControl isInvalid={!!form.formState.errors.partSpecTable}>
            <FormLabel htmlFor="part_spec_table_input">
              Part Spec Table:
            </FormLabel>

            <Select
              {...form.register('partSpecTable')}
              id="part_spec_table_input"
            >
              {Object.entries(PartSpecTable).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </Select>

            <ErrorMessage
              name="partSpecTable"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />

            <FormHelperText>
              Which table in Visual we use to look up Part Specs.
            </FormHelperText>
          </FormControl>
        */}
    </Flex>
  );
}

function showPartRequirementFixedCost(siteCode: string) {
  // Only show for CAP for now
  return [SiteCode.CAP.toString()].includes(siteCode.toUpperCase());
}
